import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GlobalStyles } from "@mui/material";
import { ThemeProvider, useTheme } from "@mui/material/styles";
import i18n from "../../i18n/config";
import {
  isDoctorSessionActive,
  isTenantSessionActive,
  isSuperAdminSessionActive,
} from "../../utils/authSession";
import {
  createPanelMuiTheme,
  isPanelLightTheme,
  isPanelTheme,
} from "./panelMuiTheme";
import { panelCssVariableStyles } from "./panelCssVars";
import {
  PANEL_THEME_STORAGE_KEY,
  persistPanelThemePreference,
  readStoredPanelPreference,
  readSystemColorScheme,
  resolvePanelThemeMode,
  subscribeSystemColorScheme,
} from "./panelThemePreference";
import {
  fetchMyPreferences,
  isPreferencesEndpointUnavailable,
  updateMyAppearance,
} from "../../services/userPreferencesService";

export { createPanelMuiTheme, isPanelLightTheme, isPanelTheme };
export { PANEL_THEME_STORAGE_KEY };

export const PanelThemeContext = createContext(null);

export function usePanelTheme() {
  const ctx = useContext(PanelThemeContext);
  if (!ctx) {
    throw new Error("usePanelTheme must be used within PanelThemeProvider");
  }
  return ctx;
}

export function useIsPanelTheme() {
  const theme = useTheme();
  return isPanelTheme(theme);
}

export function useIsPanelLightTheme() {
  const theme = useTheme();
  return isPanelLightTheme(theme);
}

function PanelMuiBridge({ children, mode }) {
  const [lang, setLang] = useState(() => (i18n.language || "en").split("-")[0]);

  useEffect(() => {
    const onLanguageChanged = (lng) => setLang((lng || "en").split("-")[0]);
    i18n.on("languageChanged", onLanguageChanged);
    return () => i18n.off("languageChanged", onLanguageChanged);
  }, []);

  const theme = useMemo(
    () => createPanelMuiTheme(lang, mode),
    [lang, mode]
  );

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}

function hasAuthToken() {
  try {
    return (
      isDoctorSessionActive() ||
      isTenantSessionActive() ||
      isSuperAdminSessionActive()
    );
  } catch {
    return false;
  }
}

/**
 * Panel CSS değişkenlerini enjekte eder, `html[data-theme]` yazar ve MUI
 * `palette.mode`'u aynı state'e bağlar.
 *
 * Tercih: light | dark | system
 * - localStorage: anında / offline cache
 * - GET/PUT /api/me/preferences: hesap bazlı cihazlar arası senkron
 * - system: OS `prefers-color-scheme` (saat değil)
 */
export default function PanelThemeProvider({ children }) {
  const parent = useContext(PanelThemeContext);

  const [preference, setPreferenceState] = useState(readStoredPanelPreference);
  const [systemMode, setSystemMode] = useState(readSystemColorScheme);
  const skipNextRemotePersist = useRef(false);
  const remoteReady = useRef(false);
  const persistTimer = useRef(null);

  const mode = useMemo(
    () => resolvePanelThemeMode(preference, systemMode),
    [preference, systemMode]
  );

  const setPreference = useCallback((next) => {
    setPreferenceState(next === "dark" || next === "system" ? next : "light");
  }, []);

  const setMode = useCallback((next) => {
    setPreferenceState(next === "dark" ? "dark" : "light");
  }, []);

  const toggleMode = useCallback(() => {
    setPreferenceState((prev) => {
      const resolved = resolvePanelThemeMode(prev, systemMode);
      return resolved === "dark" ? "light" : "dark";
    });
  }, [systemMode]);

  useEffect(() => {
    if (parent) return undefined;
    return subscribeSystemColorScheme(setSystemMode);
  }, [parent]);

  useEffect(() => {
    if (parent) return undefined;
    const onStorage = (event) => {
      if (event.key !== PANEL_THEME_STORAGE_KEY || event.newValue == null) return;
      skipNextRemotePersist.current = true;
      setPreferenceState(
        event.newValue === "dark" || event.newValue === "system"
          ? event.newValue
          : "light"
      );
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [parent]);

  // Hesap tercihi: panel açılışında sunucudan çek (varsa local'i ezer).
  useEffect(() => {
    if (parent || !hasAuthToken()) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const result = await fetchMyPreferences();
        if (cancelled) return;

        if (result.appearance) {
          skipNextRemotePersist.current = true;
          setPreferenceState(result.appearance);
          persistPanelThemePreference(result.appearance);
          remoteReady.current = true;
          return;
        }

        // Sunucuda kayıt yoksa mevcut yerel tercihi hesap kaydına yaz.
        remoteReady.current = true;
        const local = readStoredPanelPreference();
        updateMyAppearance(local).catch((error) => {
          if (!isPreferencesEndpointUnavailable(error)) {
            /* seed optional */
          }
        });
      } catch (error) {
        if (!isPreferencesEndpointUnavailable(error)) {
          // 401 axios interceptor zaten yönlendirir; diğer hatalarda local kalır.
        }
        remoteReady.current = true;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [parent]);

  useLayoutEffect(() => {
    if (parent) return undefined;
    document.documentElement.setAttribute("data-theme", mode);
    persistPanelThemePreference(preference);
    return undefined;
  }, [mode, preference, parent]);

  // Kullanıcı değişikliği → sunucuya debounce PUT (endpoint yoksa sessiz).
  useEffect(() => {
    if (parent) return undefined;
    if (skipNextRemotePersist.current) {
      skipNextRemotePersist.current = false;
      return undefined;
    }
    if (!remoteReady.current || !hasAuthToken()) return undefined;

    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      updateMyAppearance(preference).catch((error) => {
        if (!isPreferencesEndpointUnavailable(error)) {
          // Ağ hatası: local tercih zaten kalıcı; sonraki oturumda tekrar dener.
        }
      });
    }, 350);

    return () => {
      if (persistTimer.current) clearTimeout(persistTimer.current);
    };
  }, [preference, parent]);

  const value = useMemo(
    () => ({
      preference,
      mode,
      setPreference,
      setMode,
      toggleMode,
      isDark: mode === "dark",
    }),
    [preference, mode, setPreference, setMode, toggleMode]
  );

  if (parent) {
    return <PanelMuiBridge mode={parent.mode}>{children}</PanelMuiBridge>;
  }

  return (
    <PanelThemeContext.Provider value={value}>
      <GlobalStyles styles={panelCssVariableStyles} />
      <PanelMuiBridge mode={mode}>{children}</PanelMuiBridge>
    </PanelThemeContext.Provider>
  );
}
